/**
 * Admin Panel Settings Configuration
 * 
 * This file contains all the settings categories and their items for the admin panel.
 * It's separated from the component to make it easier to maintain and extend.
 */

export interface AdminSettingItemDetail {
  key: string;
  description: string;
}

export interface AdminSettingItem {
  title: string;
  list_of_items: AdminSettingItemDetail[];
}

export interface AdminSettingsConfig {
  [key: string]: AdminSettingItem;
}

/**
 * Project-specific settings configuration object
 * Add new categories and items here as needed for projects
 */
export const ADMIN_SETTINGS_CONFIG_PROJECT: AdminSettingsConfig = {
  conversation_setup: {
    title: 'Conversation Setup',
    list_of_items: [
      { key: 'CONVERSATION_MANAGE_CONVERSATION', description: 'Manage Conversation Setup' },
      { key: 'CONVERSATION_MANAGE_PUBLISH', description: 'Manage Review and Publish' }
    ]
  },
  action_setup: {
    title: 'Action Setup',
    list_of_items: [
      { key: 'ACTION_CONFIGURATIONS_SETUP', description: 'Action Configurations Setup' }
    ]
  },
  tokenization_setup: {
    title: 'Tokenization Setup',
    list_of_items: [ 
      { key: 'TOKENIZATION_SETUP', description: 'Tokenization Setup' }
    ]
  },
  sentiment_analysis: {
    title: 'Sentiment Analysis',
    list_of_items: [
      {
        key: 'SENTIMENT_ANALYSIS_MODULE_SETTINGS',
        description: 'Configure Sentiment Analysis tabs & options (gear on Sentiment Analysis screen)',
      },
    ],
  },
  manage_admin_permission:{
    title: 'Manage Admin Permission',
    list_of_items: [
      { key: 'ADMIN_PERMISSION_LIST', description: 'Admin Permission list' }
    ]
  },
  manage_organization_access: {
    title: 'Organization',
    list_of_items: [
      { key: 'MANAGE_ORGANIZATION_USER_ACCESS', description: 'Manage User Access' }
    ]
  },
  channel_setup: {
    title: 'Channel Setup',
    list_of_items: [
      { key: 'CHANNEL_MANAGE_CHANNEL_CONFIGURATION', description: 'Invites and Reminders' },
      { key: 'CHANNEL_MANAGE_FILTER_AND_TIMING', description: 'Filters and Timing' },
      { key: 'CHANNEL_MANAGE_BLACKLIST', description: 'Blacklist / DND' },
      { key: 'CHANNEL_MANAGE_UNSUBSCRIBE', description: 'Unsubscribe list' },
      { key: 'CHANNEL_MANAGE_TAG_BASED_TEMPLATE', description: 'Templates' },
    ]
  },
  manage_datasets: {
    title: 'Datasets',
    list_of_items: [
      { key: 'MANAGE_DATASETS_CREATION', description: 'Dataset Creation' },
    ]
  },
  tools: {
    title: 'Tools',
    list_of_items: [
      { key: 'TOOLS_DATA_CLEANUP', description: 'Data Cleanup' },
    ]
  }
};

/**
 * Account-specific settings configuration object
 * Add new categories and items here as needed for accounts
 */
export const ADMIN_SETTINGS_CONFIG_ACCOUNT: AdminSettingsConfig = {
  account_management: {
    title: 'Account Management',
    list_of_items: [
      { key: 'ACCOUNT_CREATION_NEW_PROJECT', description: 'Project Creation' },
      { key: 'ACCOUNT_CREATE_NEW_USER', description: 'Create New User' },
      { key: 'ACCOUNT_MANAGE_DASHBOARD_CONFIGURATION', description: 'Manage Dashboard Configuration' },
      { key: 'ACCOUNT_MANAGE_SECURITY', description: 'Manage Security' },
      { key: 'ACCOUNT_MANAGE_NOTIFICATIONS', description: 'Manage Notifications' },
      { key: 'ACCOUNT_MANAGE_SUB_ACCOUNT_CONFIGURATION', description: 'Manage Sub Account Configuration' },
      { key: 'ACCOUNT_MANAGE_ACCOUNT_INFOMATION_UPDATE', description: 'Account Infomation Update' },
    ]
  },
  sentiment_analysis: {
    title: 'Sentiment Analysis',
    list_of_items: [
      {
        key: 'SENTIMENT_ANALYSIS_MODULE_SETTINGS',
        description: 'Configure Sentiment Analysis tabs & options (gear on Sentiment Analysis screen)',
      },
    ],
  },
  relationship_view: {
    title: 'Relationship View',
    list_of_items: [
      {
        key: 'RELATIONSHIP_VIEW_TAB_SETTINGS',
        description: 'Enable Relationship View',
      },{ 
        key: 'ACCOUNT_MANAGE_RELATIONSHIP_CONFIG_UPDATE', 
        description: 'Relationship Configuration Update' 
      }
    ],
  },
  manage_admin_permission:{
    title: 'Manage Admin Permission',
    list_of_items: [
      { key: 'ADMIN_PERMISSION_LIST', description: 'Admin Permission list' }
    ]
  },
  billing_management: {
    title: 'Billing Management',
    list_of_items: [
      { key: 'BILLING_DASHBOARD', description: 'Billing Dashboard' },
      { key: 'INVOICE_GENERATION', description: 'Invoice Generation' },
    ],
  },
};

/**
 * Default Admin-specific settings configuration object
 * Add new categories and items here as needed for default_admin
 */
export const ADMIN_SETTINGS_CONFIG_DEFAULT_ADMIN: AdminSettingsConfig = {
  account_management: {
    title: 'Account Management',
    list_of_items: [
      { key: 'ACCOUNT_CREATION', description: 'Account Creation' },
      { key: 'ADMIN_PERMISSION_PROVIDE', description: 'Add to manage the console panel' }
    ]
  },
  billing_management: {
    title: 'Billing Management',
    list_of_items: [
      { key: 'BILLING_DASHBOARD', description: 'Billing Dashboard' },
      { key: 'INVOICE_GENERATION', description: 'Invoice Generation' }
    ]
  }
};

/**
 * Backward compatibility - defaults to project config
 * @deprecated Use ADMIN_SETTINGS_CONFIG_PROJECT or ADMIN_SETTINGS_CONFIG_ACCOUNT instead
 */
export const ADMIN_SETTINGS_CONFIG = ADMIN_SETTINGS_CONFIG_PROJECT;

/**
 * Order of categories for rendering - Project context
 * Add new categories here in the desired order
 */
export const ADMIN_SETTINGS_CATEGORY_ORDER_PROJECT: string[] = [
  'conversation_setup', 
  'manage_organization_access',
  'channel_setup',
  'manage_datasets',
  'tools',
  'action_setup',
  'tokenization_setup',
  'sentiment_analysis',
  'manage_admin_permission'
];

/**
 * Order of categories for rendering - Account context
 * Add new categories here in the desired order
 */
export const ADMIN_SETTINGS_CATEGORY_ORDER_ACCOUNT: string[] = [
  'account_management',
  'sentiment_analysis',
  'billing_management',
  'relationship_view',
  'manage_admin_permission'
];

/**
 * Order of categories for rendering - Default Admin context
 * Add new categories here in the desired order
 */
export const ADMIN_SETTINGS_CATEGORY_ORDER_DEFAULT_ADMIN: string[] = [
  'account_management',
  'billing_management'
];

/**
 * Backward compatibility - defaults to project order
 * @deprecated Use ADMIN_SETTINGS_CATEGORY_ORDER_PROJECT or ADMIN_SETTINGS_CATEGORY_ORDER_ACCOUNT instead
 */
export const ADMIN_SETTINGS_CATEGORY_ORDER = ADMIN_SETTINGS_CATEGORY_ORDER_PROJECT;

/**
 * Permission type options
 */
export const ADMIN_PERMISSION_TYPE_OPTIONS: string[] = ['READ', 'WRITE'];

/**
 * Hierarchy access options
 */
export const ADMIN_HIERARCHY_OPTIONS: string[] = [
  'Hierarchy Level Access',
  'Hierarchy Value Access',
  'Custom'
];

/**
 * Helper function to get all unique permission keys from all categories
 * @param context - 'project', 'account', or 'default_admin' context
 */
export function getAllPermissionKeys(context: 'project' | 'account' | 'default_admin' = 'project'): string[] {
  const allKeys: string[] = [];
  let config: AdminSettingsConfig;

  if (context === 'account') {
    config = ADMIN_SETTINGS_CONFIG_ACCOUNT;
  } else if (context === 'default_admin') {
    config = ADMIN_SETTINGS_CONFIG_DEFAULT_ADMIN;
  } else {
    config = ADMIN_SETTINGS_CONFIG_PROJECT;
  }

  Object.values(config).forEach(category => {
    allKeys.push(...category.list_of_items.map(item => item.key));
  });

  return allKeys;
}

/**
 * Helper function to get all unique permission items (with descriptions) from all categories
 * @param context - 'project', 'account', or 'default_admin' context
 */
export function getAllPermissionItems(context: 'project' | 'account' | 'default_admin' = 'project'): AdminSettingItemDetail[] {
  const allItems: AdminSettingItemDetail[] = [];
  let config: AdminSettingsConfig;

  if (context === 'account') {
    config = ADMIN_SETTINGS_CONFIG_ACCOUNT;
  } else if (context === 'default_admin') {
    config = ADMIN_SETTINGS_CONFIG_DEFAULT_ADMIN;
  } else {
    config = ADMIN_SETTINGS_CONFIG_PROJECT;
  }

  Object.values(config).forEach(category => {
    allItems.push(...category.list_of_items);
  });

  return allItems;
}

/**
 * Helper function to get category key by item key
 * @param itemKey - The item key to search for
 * @param context - 'project', 'account', or 'default_admin' context
 */
export function getCategoryKeyByItemKey(itemKey: string, context: 'project' | 'account' | 'default_admin' = 'project'): string | null {
  let config: AdminSettingsConfig;

  if (context === 'account') {
    config = ADMIN_SETTINGS_CONFIG_ACCOUNT;
  } else if (context === 'default_admin') {
    config = ADMIN_SETTINGS_CONFIG_DEFAULT_ADMIN;
  } else {
    config = ADMIN_SETTINGS_CONFIG_PROJECT;
  }

  for (const [categoryKey, category] of Object.entries(config)) {
    if (category.list_of_items.some(item => item.key === itemKey)) {
      return categoryKey;
    }
  }
  return null;
}

/**
 * Helper function to get category title by item key
 * @param itemKey - The item key to search for
 * @param context - 'project', 'account', or 'default_admin' context
 */
export function getCategoryTitleByItemKey(itemKey: string, context: 'project' | 'account' | 'default_admin' = 'project'): string | null {
  const categoryKey = getCategoryKeyByItemKey(itemKey, context);
  let config: AdminSettingsConfig;

  if (context === 'account') {
    config = ADMIN_SETTINGS_CONFIG_ACCOUNT;
  } else if (context === 'default_admin') {
    config = ADMIN_SETTINGS_CONFIG_DEFAULT_ADMIN;
  } else {
    config = ADMIN_SETTINGS_CONFIG_PROJECT;
  }

  return categoryKey ? config[categoryKey].title : null;
}

/**
 * Helper function to get item description by key
 * @param itemKey - The item key to search for
 * @param context - 'project', 'account', or 'default_admin' context
 */
export function getItemDescriptionByKey(itemKey: string, context: 'project' | 'account' | 'default_admin' = 'project'): string | null {
  let config: AdminSettingsConfig;

  if (context === 'account') {
    config = ADMIN_SETTINGS_CONFIG_ACCOUNT;
  } else if (context === 'default_admin') {
    config = ADMIN_SETTINGS_CONFIG_DEFAULT_ADMIN;
  } else {
    config = ADMIN_SETTINGS_CONFIG_PROJECT;
  }

  for (const category of Object.values(config)) {
    const item = category.list_of_items.find(item => item.key === itemKey);
    if (item) {
      return item.description;
    }
  }
  return null;
}

/**
 * Helper function to get item details by key
 * @param itemKey - The item key to search for
 * @param context - 'project', 'account', or 'default_admin' context
 */
export function getItemByKey(itemKey: string, context: 'project' | 'account' | 'default_admin' = 'project'): AdminSettingItemDetail | null {
  let config: AdminSettingsConfig;

  if (context === 'account') {
    config = ADMIN_SETTINGS_CONFIG_ACCOUNT;
  } else if (context === 'default_admin') {
    config = ADMIN_SETTINGS_CONFIG_DEFAULT_ADMIN;
  } else {
    config = ADMIN_SETTINGS_CONFIG_PROJECT;
  }

  for (const category of Object.values(config)) {
    const item = category.list_of_items.find(item => item.key === itemKey);
    if (item) {
      return item;
    }
  }
  return null;
}
